import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useEmpresaStore } from '../../../store/empresaStore';
import './EmpresasPanel.css';

export default function EmpresasPanel() {
  const { user } = useAuth();
  const { empresa, loading, error, submitting, success, fetchEmpresa, updateEmpresa } = useEmpresaStore();
  const [form, setForm] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFileName, setLogoFileName] = useState('');
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchEmpresa(user);
  }, [user, fetchEmpresa]);

  useEffect(() => {
    if (empresa) {
      setForm(empresa);
      if (empresa.Imagen && empresa.Imagen.RutaView) {
        setLogoPreview(empresa.Imagen.RutaView);
      }
    }
  }, [empresa]);

  useEffect(() => {
    if (success) {
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);
    }
  }, [success]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'telefono') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setForm(prev => ({ ...prev, [name]: numericValue }));
      }
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        alert('El archivo es demasiado grande. Máximo 1MB.');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert('Por favor seleccione una imagen válida (PNG o JPG).');
        return;
      }

      setLogoFileName(file.name);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setLogoPreview(base64String);
        setForm(prev => ({ ...prev, logo: base64String }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateEmpresa(form);
  };

  if (loading || !form) return (
    <div className="config-feedback">
      <div className="loading-spinner"></div>
      <p>Cargando datos de la empresa…</p>
    </div>
  );

  return (
    <form className="empresa-form-layout" onSubmit={handleSubmit}>
      <div className="empresa-top-row">
        <aside className="empresa-sidebar-card">
          <div className="empresa-logo-container" onClick={handleFileButtonClick} style={{ cursor: 'pointer' }}>
            <div className="empresa-logo-placeholder">
              {logoPreview || (form.Imagen && form.Imagen.RutaView) || form.logo ? (
                <img 
                  src={logoPreview || (form.Imagen && form.Imagen.RutaView) || form.logo} 
                  alt="Logo" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px' }} 
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="material-symbols-rounded" style="font-size: 3rem">domain</span>';
                  }}
                />
              ) : (
                <span className="material-symbols-rounded" style={{ fontSize: '3rem' }}>domain</span>
              )}
            </div>
          </div>
          
          <h2 className="empresa-sidebar-name">{form.nombre || 'Nombre Empresa'}</h2>
          <p className="empresa-logo-hint">PNG o JPG (1MB máx)</p>

          <div className="empresa-sidebar-stats">
            <div className="empresa-stat-item">
              <span>RFC</span>
              <strong>{form.rfc || '—'}</strong>
            </div>
            <div className="empresa-stat-item">
              <span>Estado de Cuenta</span>
              <div className="empresa-status-tag">
                <span className="material-symbols-rounded">verified</span> Activa
              </div>
            </div>
            <div className="empresa-stat-item">
              <span>Última Actualización</span>
              <div className="empresa-date-info">
                <span className="material-symbols-rounded">calendar_month</span>
                {new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            </div>
          </div>
        </aside>

        <div className="empresa-main-section">
          <div className="empresa-section">
            <header className="empresa-section-header">
              <span className="material-symbols-rounded">business</span> Datos de la Empresa
            </header>
            <div className="empresa-grid">
              <label className="empresa-field empresa-field--full">
                <span>Nombre de la Empresa*</span>
                <input name="nombre" value={form.nombre} onChange={handleChange} placeholder="Ej. SmartNet S.A." required />
              </label>
              <label className="empresa-field">
                <span>RFC*</span>
                <input name="rfc" value={form.rfc} onChange={handleChange} placeholder="VIML..." required />
              </label>
              <label className="empresa-field">
                <span>Teléfono (10 dígitos)</span>
                <input 
                  type="tel" 
                  name="telefono" 
                  value={form.telefono} 
                  onChange={handleChange} 
                  placeholder="9991234567" 
                  pattern="[0-9]{10}" 
                  maxLength="10"
                  title="Ingrese exactamente 10 dígitos numéricos"
                />
              </label>
              <label className="empresa-field">
                <span>Razón Social*</span>
                <input name="razonSocial" value={form.razonSocial} onChange={handleChange} placeholder="Nombre legal completo" required />
              </label>
              <label className="empresa-field">
                <span>Correo Electrónico</span>
                <input type="email" name="correo" value={form.correo} onChange={handleChange} placeholder="contacto@empresa.com" />
              </label>
              <div className="empresa-field empresa-field--full">
                <span>Logotipo</span>
                <div className="empresa-file-input">
                  <input 
                    type="text" 
                    readOnly 
                    value={logoFileName || 'Seleccionar nuevo logotipo...'} 
                    placeholder="Seleccionar nuevo logotipo..." 
                  />
                  <input 
                    ref={fileInputRef}
                    type="file" 
                    accept="image/png, image/jpeg, image/jpg" 
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <button type="button" className="empresa-file-btn" onClick={handleFileButtonClick}>
                    <span className="material-symbols-rounded">upload_file</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="empresa-full-width-sections">
        {error && <p className="config-feedback config-feedback--error">{error}</p>}
        {success && <p className="config-feedback config-feedback--ok">{success}</p>}

        <div className="empresa-section empresa-section--locked">
          <header className="empresa-section-header">
            <span className="material-symbols-rounded">location_on</span> Domicilio Fiscal
            <div className="empresa-locked-badge">
              <span className="material-symbols-rounded">lock</span>
              Bloqueado
            </div>
          </header>
          <div className="empresa-grid empresa-grid--4cols">
            <label className="empresa-field empresa-field--span2">
              <span>Calle</span>
              <input name="calle" value={form.calle} onChange={handleChange} placeholder="Ej. Calle 60 por 57" readOnly />
            </label>
            <label className="empresa-field">
              <span>Número Exterior</span>
              <input name="numExt" value={form.numExt} onChange={handleChange} placeholder="S/N o Número" readOnly />
            </label>
            <label className="empresa-field">
              <span>Número Interior</span>
              <input name="numInt" value={form.numInt} onChange={handleChange} placeholder="Dpto / Local" readOnly />
            </label>
            <label className="empresa-field">
              <span>Referencias (Extra)</span>
              <input name="extra" value={form.extra} onChange={handleChange} placeholder="Ej. Frente al parque" readOnly />
            </label>
            <label className="empresa-field">
              <span>Entre Calles</span>
              <input name="entre" value={form.entre} onChange={handleChange} placeholder="Calle X y Calle Y" readOnly />
            </label>
            <label className="empresa-field">
              <span>Colonia</span>
              <input name="colonia" value={form.colonia} onChange={handleChange} placeholder="Ej. Centro" readOnly />
            </label>
            <label className="empresa-field">
              <span>Código Postal</span>
              <input name="cp" value={form.cp} onChange={handleChange} placeholder="97000" readOnly />
            </label>
            <label className="empresa-field">
              <span>País</span>
              <input name="pais" value={form.pais} onChange={handleChange} placeholder="México" readOnly />
            </label>
            <label className="empresa-field">
              <span>Estado</span>
              <input name="estado" value={form.estado} onChange={handleChange} placeholder="Yucatán" readOnly />
            </label>
          </div>
        </div>
      </div>

      <div className="empresa-fixed-actions">
        <div className="empresa-actions-container">
          <div className="empresa-actions-info">
            <span className="material-symbols-rounded">info</span>
            Los cambios se aplicarán de forma inmediata en todo el sistema.
          </div>
          <button type="submit" className="empresa-save-btn" disabled={submitting}>
            <span className="material-symbols-rounded">
              {submitting ? 'hourglass_empty' : 'save'}
            </span> 
            {submitting ? 'GUARDANDO CAMBIOS...' : 'GUARDAR CAMBIOS'}
          </button>
        </div>
      </div>

      {showSuccessModal && (
        <div className="empresa-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="empresa-modal-success" onClick={(e) => e.stopPropagation()}>
            <div className="empresa-modal-icon">
              <span className="material-symbols-rounded">check_circle</span>
            </div>
            <h3>¡Datos Actualizados!</h3>
            <p>Los cambios se han guardado exitosamente</p>
            <button 
              className="empresa-modal-btn" 
              onClick={() => setShowSuccessModal(false)}
            >
              Aceptar
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
